import { CancelReason, DEFAULT_LANG, Role, SIDE_CHANNEL_KINDS, } from "@gigsync/protocol";
import { ulid } from "ulid";
import { ContactKeeper } from "../contacts/keeper.js";
import { apply } from "../engagement/machine.js";
import { MailTransport } from "../mail/transport.js";
import { Mentors } from "../mentor/mentors.js";
import { everyDeviceSpeaks } from "../net/reach.js";
import { PaymentBook } from "../payments/book.js";
import { WorkOffers } from "../qr/offers.js";
import { endRestored } from "../records/follow.js";
import { RecordsRunner } from "../records/runner.js";
import * as after from "./aftercare.js";
import { claimFor } from "./claim.js";
import * as consumer from "./consumer.js";
import * as deal from "./deal.js";
import { dispatch, needOf } from "./dispatch.js";
import { stamped } from "./history.js";
import { JobStore, TaskKind } from "./store.js";
import { Talks } from "./talks.js";
import * as worker from "./worker.js";
import { confirm, reofferTaken } from "./worker-time.js";
export class DeviceError extends Error {
}
/** One phone's agent for jobs: it negotiates on the owner's behalf and asks the owner only to decide. */
export class GigDevice {
    o;
    store;
    payments;
    /** Mentors for someone new to a city: introductions, listings, landing requests, mentor links and their notes. */
    mentors;
    /** Offers to work through a personal QR: a worker offers, the household answers by asking (ADR 0034). */
    offers;
    /** Sends this owner's job records to GigSync once they agreed; absent without the server's routes. */
    records;
    /** Contact details of settled jobs, removed on sync, and the problems that hold them (ADR 0039 §3). */
    contacts;
    /** The owner takes a talk over from their agent, or decides a time (ADR 0053), and why an offer is what it is. */
    talks;
    transport;
    ctx;
    constructor(o) {
        this.o = o;
        this.store = new JobStore(o.storage);
        this.payments = new PaymentBook(o.storage, o.clock);
        this.transport = new MailTransport(o.relay, o.storage, o.keys, o.clock, o.me);
        this.ctx = {
            me: o.me,
            contact: o.contact,
            lang: o.lang ?? DEFAULT_LANG,
            autoConfirm: o.autoConfirm ?? false,
            store: this.store,
            storage: o.storage,
            payments: this.payments,
            keys: o.keys,
            clock: o.clock,
            random: o.random,
            ...(o.model ? { model: o.model } : {}),
            ...(o.estimates ? { estimates: o.estimates } : {}),
            speaks: (user, protocol) => everyDeviceSpeaks(o.relay, user, protocol),
            send: (r, kind, body) => this.send(r, kind, body),
            claim: (engagement) => claimFor(o.relay, engagement, o.me.device),
            signingKeyOf: (user, device) => this.transport.signingKeyOf(user, device),
        };
        this.mentors = new Mentors({ ctx: this.ctx, transport: this.transport, relay: o.relay }, o.mentorApi);
        this.offers = new WorkOffers({ ctx: this.ctx, transport: this.transport, isWorker: !!o.worker }, o.qrApi);
        this.contacts = new ContactKeeper(this.ctx);
        this.talks = new Talks(this.ctx);
        if (o.records)
            this.records = new RecordsRunner(o.records, o.storage, o.clock, {
                jobs: () => this.jobs(),
                payments: () => this.payments.all(),
            });
    }
    /** Takes in what arrived, lets the agent answer it, and sends what is queued. */
    async sync() {
        const received = await this.transport.receive();
        const side = { mentors: this.mentors, offers: this.offers, contacts: this.contacts };
        // In order on purpose: each envelope may depend on the one before it.
        for (const item of received)
            await dispatch(this.ctx, this.o.worker, item, side);
        // A time given to one household is never signed for another (ADR 0048 §5).
        if (this.o.worker)
            await reofferTaken(this.ctx, this.o.worker);
        await this.contacts.sweep();
        const sent = await this.transport.flush();
        await this.records?.run();
        return { received: received.length, sent };
    }
    // ── Consumer ──────────────────────────────────────────────────────────────────────────────────────────────────────
    /** Sends the need to one or more workers at once; agreeing with one cancels the rest. */
    async request(n, workers) {
        if (workers.length === 0)
            throw new DeviceError("no workers to ask");
        if (workers.includes(this.o.me.user))
            throw new DeviceError("a phone cannot hire its own owner");
        const need = { ...n, details: n.details ?? {}, group: `eng_${ulid(this.o.clock.now().getTime())}` };
        await this.store.saveNeed(need);
        // Each request is its own engagement with its own worker, so they can be prepared and queued together.
        return Promise.all(workers.map((w) => consumer.startRequest(this.ctx, need, w)));
    }
    async answer(id, answers) {
        const r = await this.job(id);
        return consumer.answer(this.ctx, await needOf(this.ctx, r), r, answers);
    }
    async upiLink(paymentId) {
        return after.upiLink(this.ctx, paymentId);
    }
    async markPaid(paymentId, utr) {
        const p = await this.payments.markPaid(paymentId, utr);
        await after.sendPaymentRecord(this.ctx, await this.job(p.engagement), p);
        this.records?.soon();
        return p;
    }
    async review(id, review) {
        const r = await after.sendReview(this.ctx, await this.job(id), { stars: review.stars, text: review.text ?? "" });
        this.records?.soon();
        return r;
    }
    // ── Worker ────────────────────────────────────────────────────────────────────────────────────────────────────────
    /** A progress note: a common code (`{ code: "on_my_way" }`) or the worker's own words. */
    async update(id, say) {
        return after.sendUpdate(this.ctx, await this.job(id), say);
    }
    async deliver(id, say) {
        return after.sendDelivery(this.ctx, await this.job(id), say);
    }
    /** Asks for the signed price, with the UPI ID saved in settings; throws `PayeeMissingError` when there is none. */
    async requestPayment(id, note) {
        this.rules();
        return after.requestPayment(this.ctx, await this.provided(id), note);
    }
    /** The customer paid in cash: recorded as cash and confirmed on both phones, asking first if nobody had asked. */
    async recordCash(id) {
        this.rules();
        return after.recordCash(this.ctx, await this.provided(id));
    }
    /** The UPI link for the worker's own QR on an open payment, from the payee saved in settings. */
    async myUpiLink(paymentId) {
        this.rules();
        return after.myUpiLink(this.ctx, paymentId);
    }
    /** Takes a request the worker's rules said to show them first; the agent then quotes. */
    async approveRequest(id) {
        return worker.approve(this.ctx, this.rules(), await this.job(id));
    }
    /** Turns down a request the worker's rules said to show them first. */
    async declineRequest(id) {
        this.rules();
        return worker.refuse(this.ctx, await this.job(id));
    }
    async confirmPayment(paymentId) {
        return this.settlePayment(paymentId, (id) => this.payments.confirm(id));
    }
    async disputePayment(paymentId) {
        return this.settlePayment(paymentId, (id) => this.payments.dispute(id));
    }
    // ── Either side ───────────────────────────────────────────────────────────────────────────────────────────────────
    tasks() {
        return this.store.tasks();
    }
    async confirmDeal(id) {
        const r = await confirm(this.ctx, this.o.worker, await this.job(id));
        if (this.o.worker)
            await reofferTaken(this.ctx, this.o.worker);
        this.records?.soon();
        return r;
    }
    async declineDeal(id) {
        return deal.decline(this.ctx, await this.job(id));
    }
    /** Ends a job restored from the record from its own card; the record carries it to the other side (follow.ts). */
    async endRestored(id, end) {
        const r = await endRestored(this.store, await this.job(id), end, this.o.clock.now());
        this.records?.soon();
        return r;
    }
    async cancelJob(id, note) {
        return deal.cancel(this.ctx, await this.job(id), { reason: CancelReason.enum.other, ...(note ? { note } : {}) });
    }
    jobs() {
        return this.store.jobs();
    }
    async job(id) {
        const r = await this.store.job(id);
        if (!r)
            throw new DeviceError(`no job ${id}`);
        return r;
    }
    // ── Internals ─────────────────────────────────────────────────────────────────────────────────────────────────────
    rules() {
        if (!this.o.worker)
            throw new DeviceError("only a worker's phone does this");
        return this.o.worker;
    }
    /** A job this phone does as the worker; on a job where it is the requester, it is the one who pays. */
    async provided(id) {
        const r = await this.job(id);
        if (r.engagement.role !== Role.enum.provider)
            throw new DeviceError(`not the worker on ${id}`);
        return r;
    }
    async settlePayment(paymentId, change) {
        const p = await change(paymentId);
        await this.store.removeTask(TaskKind.confirmPayment, p.engagement);
        await after.sendPaymentRecord(this.ctx, await this.job(p.engagement), p);
        return p;
    }
    /** Applies the move first (so an impossible one throws before anything is queued), then seals and queues it. */
    async send(r, kind, body) {
        const now = this.o.clock.now();
        const engagement = SIDE_CHANNEL_KINDS.has(kind) ? r.engagement : apply(r.engagement, kind, now);
        await this.transport.send(r.engagement.counterpart, r.engagement.id, kind, body);
        const next = stamped({ ...r, engagement }, now.toISOString());
        await this.store.saveJob(next);
        return next;
    }
}
//# sourceMappingURL=device.js.map